import { Link } from 'react-router-dom'
import { usePortalThemeValues } from '@/hooks/usePortalTheme'

/* ==========================================================================
   Pharmacy Logo Component
   Task F30: White-Label Theme System

   Displays the pharmacy logo from portal config with graceful fallback.
   ========================================================================== */

export interface PharmacyLogoProps {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** Link destination (optional) */
  linkTo?: string
  /** Additional CSS classes */
  className?: string
}

/**
 * Displays the pharmacy logo from portal config.
 * Falls back to pharmacy name in styled text if no logo URL is configured.
 *
 * @example
 * ```tsx
 * // On login page (large logo)
 * <PharmacyLogo size="lg" linkTo="/" />
 *
 * // In header (medium logo)
 * <PharmacyLogo size="md" />
 * ```
 */
export function PharmacyLogo({
  size = 'md',
  linkTo,
  className = '',
}: PharmacyLogoProps) {
  const { logoUrl, pharmacyName, loading } = usePortalThemeValues()

  // Size classes for logo image and fallback text
  const sizeConfig = {
    sm: {
      imgClass: 'h-8 max-w-[150px]',
      textClass: 'text-lg',
    },
    md: {
      imgClass: 'h-10 max-w-[200px]',
      textClass: 'text-xl',
    },
    lg: {
      imgClass: 'h-15 max-w-[300px]',
      textClass: 'text-2xl',
    },
    // 1.5x of lg — used on provider-facing auth pages (PORTAL-66)
    xl: {
      imgClass: 'h-[90px] max-w-full md:max-w-[450px]',
      textClass: 'text-4xl',
    },
  }

  const { imgClass, textClass } = sizeConfig[size]

  // Show loading skeleton while loading config
  if (loading) {
    return (
      <div
        className={`animate-pulse bg-gray-200 rounded ${imgClass} w-32 ${className}`}
      />
    )
  }

  // Render logo content
  const logoContent = logoUrl ? (
    <img
      src={logoUrl}
      alt={pharmacyName}
      className={`${imgClass} w-auto object-contain`}
    />
  ) : (
    <span
      className={`font-serif ${textClass} text-[var(--pharmacy-primary)]`}
    >
      {pharmacyName}
    </span>
  )

  // Wrap in link if destination provided
  if (linkTo) {
    return (
      <Link
        to={linkTo}
        className={`inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pharmacy-secondary)] rounded ${className}`}
      >
        {logoContent}
      </Link>
    )
  }

  return <div className={`inline-flex items-center ${className}`}>{logoContent}</div>
}

export default PharmacyLogo
